import { useState } from 'react'
import { useParams, Link } from 'react-router'
import {
  ArrowLeft,
  Bot,
  MessageSquare,
  Workflow,
  Wrench,
  AudioLines,
  ClipboardList,
  Gauge,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useAgent,
  useAgentVersion,
  useAgentVersionEval,
  type AgentVersion,
  type AgentVersionPrompt,
  type AgentState,
  type AgentTool,
  type AgentPostCallField,
  type AgentVoice,
  type AgentVersionEval,
} from '../api/agents'
import { Badge } from '../components/ui/Badge'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { formatPhone } from '../lib/utils'

export function AgentDetailPage() {
  const { t } = useTranslation('agents')
  const { agentId } = useParams()
  const { data: agent, isLoading, isError, refetch } = useAgent(agentId)

  // The user's explicit version choice, scoped to the agent it was made on.
  // The same AgentDetailPage instance is reused across `:agentId` param
  // changes, so we tag the choice with its agentId and ignore it when the
  // param moves on — a version picked on one agent can't leak to another that
  // lacks it. Deriving this (rather than resetting via an effect) keeps the
  // render pure and sidesteps set-state-in-effect.
  const [versionOverride, setVersionOverride] = useState<
    { agentId: string; version: number } | undefined
  >(undefined)

  if (isLoading) return <SpinLoader label={t('detail.loading')} />

  if (isError || !agent) {
    return (
      <div className="space-y-4">
        <BackLink />
        <ErrorCard message={t('detail.notFound')} onRetry={() => refetch()} />
      </div>
    )
  }

  const versions = agent.versions ?? []
  const defaultVersion =
    agent.published_version ??
    versions.find((v) => v.is_published)?.version ??
    versions[0]?.version
  const selectedVersion =
    versionOverride && versionOverride.agentId === agent.agent_id
      ? versionOverride.version
      : defaultVersion

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 md:gap-4">
        <BackLink />
        <h1 className="inline-flex items-center gap-2 text-xl font-bold text-text-primary md:text-2xl">
          <Bot className="h-5 w-5 text-text-muted" />
          {agent.agent_name || <span className="font-mono text-lg">{agent.agent_id}</span>}
        </h1>
        {(agent.purpose || agent.kind) && (
          <Badge tone={agent.kind === 'inbound' ? 'info' : agent.kind === 'outbound' ? 'flamingo' : 'neutral'}>
            {agent.purpose || agent.kind}
          </Badge>
        )}
      </div>

      {/* Summary + version picker */}
      <div className="rounded-lg border border-border bg-surface-card p-5">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Meta label={t('detail.summary.agentId')} value={agent.agent_id} mono />
          <Meta label={t('detail.summary.language')} value={agent.language} />
          <Meta label={t('detail.summary.voice')} value={agent.voice_id} mono />
          <Meta
            label={t('detail.summary.publishedVersion')}
            value={agent.published_version != null ? String(agent.published_version) : undefined}
          />
          <Meta
            label={t('detail.summary.phoneNumbers')}
            value={agent.phone_numbers?.map(formatPhone).join(', ')}
          />
        </div>

        <div className="mt-5 border-t border-border pt-4">
          <VersionPicker
            versions={versions}
            value={selectedVersion}
            onChange={(v) => setVersionOverride({ agentId: agent.agent_id, version: v })}
          />
        </div>
      </div>

      {selectedVersion != null && (
        <>
          <VersionConfig agentId={agent.agent_id} version={selectedVersion} />
          <EvaluationCard agentId={agent.agent_id} version={selectedVersion} />
        </>
      )}
    </div>
  )
}

function BackLink() {
  const { t } = useTranslation('agents')
  return (
    <Link
      to="/app/agents"
      className="flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-primary"
    >
      <ArrowLeft className="h-4 w-4" />
      {t('detail.back')}
    </Link>
  )
}

function VersionPicker({
  versions,
  value,
  onChange,
}: {
  versions: AgentVersion[]
  value: number | undefined
  onChange: (v: number) => void
}) {
  const { t } = useTranslation('agents')
  const selected = versions.find((v) => v.version === value)

  return (
    <div>
      <label className="mb-1 flex items-center gap-1.5 text-xs font-medium text-text-secondary">
        <Workflow className="h-3.5 w-3.5" />
        {t('detail.version.label')}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={value ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={versions.length === 0}
          className="min-w-[16rem] rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {versions.length === 0 && <option value="">{t('detail.version.none')}</option>}
          {versions.map((v) => (
            <option key={v.version} value={v.version}>
              {versionLabel(v, t)}
            </option>
          ))}
        </select>
        {selected?.is_published && <Badge tone="success">{t('detail.version.published')}</Badge>}
      </div>
      {selected?.version_description && (
        <p className="mt-2 text-xs text-text-muted">{selected.version_description}</p>
      )}
    </div>
  )
}

function versionLabel(v: AgentVersion, t: (k: string, o?: Record<string, unknown>) => string): string {
  const base = v.version_title || t('detail.version.numbered', { version: v.version })
  return v.is_published ? `${base} · ${t('detail.version.publishedShort')}` : base
}

// --- Per-version config -----------------------------------------------------

function VersionConfig({ agentId, version }: { agentId: string; version: number }) {
  const { t } = useTranslation('agents')
  const { data, isLoading, isError, refetch } = useAgentVersion(agentId, version)

  if (isLoading) return <SpinLoader label={t('detail.config.loading')} />
  if (isError || !data) return <ErrorCard message={t('detail.config.loadFailed')} onRetry={() => refetch()} />

  const sections = data.sections ?? {}

  return (
    <div className="space-y-4">
      <PromptCard prompt={sections.prompt} />
      <StatesCard states={sections.states} />
      <ToolsCard tools={sections.tools} />
      <VoiceCard voice={sections.voice_audio} />
      <PostCallCard fields={sections.post_call_analysis} />
    </div>
  )
}

function Card({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-border bg-surface-card p-5">
      <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-text-primary">
        <Icon className="h-4 w-4 text-text-muted" />
        {title}
      </h2>
      {children}
    </section>
  )
}

function PromptCard({ prompt }: { prompt?: AgentVersionPrompt | null }) {
  const { t } = useTranslation('agents')
  return (
    <Card icon={MessageSquare} title={t('detail.sections.prompt')}>
      <div className="space-y-4">
        <div>
          <p className="text-xs font-medium text-text-muted">{t('detail.prompt.beginMessage')}</p>
          {prompt?.begin_message ? (
            <p className="mt-1 text-sm text-text-primary">{prompt.begin_message}</p>
          ) : (
            <p className="mt-1 text-sm italic text-text-muted">—</p>
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-text-muted">{t('detail.prompt.general')}</p>
          {prompt?.general_prompt ? (
            <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-surface p-3 text-sm text-text-primary">
              {prompt.general_prompt}
            </pre>
          ) : (
            <p className="mt-1 text-sm italic text-text-muted">—</p>
          )}
        </div>
      </div>
    </Card>
  )
}

function StatesCard({ states }: { states?: AgentState[] | null }) {
  const { t } = useTranslation('agents')
  return (
    <Card icon={Workflow} title={t('detail.sections.states')}>
      {states && states.length > 0 ? (
        <div className="space-y-3">
          {states.map((state, i) => (
            <div key={(state.name as string) || i} className="rounded-lg border border-border bg-surface p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-text-primary">
                  {(state.name as string) || t('detail.states.unnamed', { index: i + 1 })}
                </span>
                {Array.isArray(state.edges) && state.edges.length > 0 && (
                  <span className="text-xs text-text-muted">
                    {t('detail.states.edgeCount', { count: state.edges.length })}
                  </span>
                )}
              </div>
              {typeof state.state_prompt === 'string' && state.state_prompt && (
                <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap text-xs text-text-secondary">
                  {state.state_prompt}
                </pre>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyLine label={t('detail.states.empty')} />
      )}
    </Card>
  )
}

function ToolsCard({ tools }: { tools?: AgentTool[] | null }) {
  const { t } = useTranslation('agents')
  return (
    <Card icon={Wrench} title={t('detail.sections.tools')}>
      {tools && tools.length > 0 ? (
        <ul className="space-y-2">
          {tools.map((tool, i) => (
            <li
              key={(tool.name as string) || i}
              className="rounded-lg border border-border bg-surface p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-text-primary">
                  {tool.name || t('detail.tools.unnamed', { index: i + 1 })}
                </span>
                {tool.type && <Badge tone="neutral">{tool.type}</Badge>}
              </div>
              {tool.description && (
                <p className="mt-1 text-xs text-text-secondary">{tool.description}</p>
              )}
              {tool.url && <p className="mt-1 break-all font-mono text-xs text-text-muted">{tool.url}</p>}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyLine label={t('detail.tools.empty')} />
      )}
    </Card>
  )
}

// Human-friendly labels for the well-known voice/audio keys. Any extra keys the
// backend passes through are rendered with their raw key name.
const VOICE_KEY_ORDER = [
  'voice_id',
  'voice_speed',
  'interruption_sensitivity',
  'responsiveness',
  'denoising_mode',
]

function VoiceCard({ voice }: { voice?: AgentVoice | null }) {
  const { t } = useTranslation('agents')
  const entries = voice
    ? Object.entries(voice).filter(([, v]) => v != null && v !== '')
    : []
  // Stable order: known keys first (in VOICE_KEY_ORDER), then the rest.
  entries.sort(([a], [b]) => {
    const ia = VOICE_KEY_ORDER.indexOf(a)
    const ib = VOICE_KEY_ORDER.indexOf(b)
    if (ia === -1 && ib === -1) return a.localeCompare(b)
    if (ia === -1) return 1
    if (ib === -1) return -1
    return ia - ib
  })

  return (
    <Card icon={AudioLines} title={t('detail.sections.voice')}>
      {entries.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {entries.map(([key, value]) => (
            <Meta
              key={key}
              label={humanizeKey(key)}
              value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
              mono={key === 'voice_id'}
            />
          ))}
        </div>
      ) : (
        <EmptyLine label={t('detail.voice.empty')} />
      )}
    </Card>
  )
}

function PostCallCard({ fields }: { fields?: AgentPostCallField[] | null }) {
  const { t } = useTranslation('agents')
  return (
    <Card icon={ClipboardList} title={t('detail.sections.postCall')}>
      {fields && fields.length > 0 ? (
        <ul className="space-y-2">
          {fields.map((field, i) => (
            <li
              key={(field.name as string) || i}
              className="rounded-lg border border-border bg-surface p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-text-primary">
                  {field.name || t('detail.postCall.unnamed', { index: i + 1 })}
                </span>
                {field.type && <Badge tone="neutral">{field.type}</Badge>}
              </div>
              {field.description && (
                <p className="mt-1 text-xs text-text-secondary">{field.description}</p>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <EmptyLine label={t('detail.postCall.empty')} />
      )}
    </Card>
  )
}

// --- Evaluation -------------------------------------------------------------

function EvaluationCard({ agentId, version }: { agentId: string; version: number }) {
  const { t } = useTranslation('agents')
  const { data, isLoading, isError, refetch } = useAgentVersionEval(agentId, version)

  return (
    <Card icon={Gauge} title={t('detail.sections.eval')}>
      {isLoading && <SpinLoader label={t('detail.eval.loading')} />}
      {isError && <ErrorCard message={t('detail.eval.loadFailed')} onRetry={() => refetch()} />}
      {!isLoading && !isError && <EvaluationBody data={data} />}
    </Card>
  )
}

function EvaluationBody({ data }: { data?: AgentVersionEval }) {
  const { t } = useTranslation('agents')
  const passive = data?.passive
  const active = data?.active ?? []

  const successRate = passive?.call_successful_rate
  const sentiment = passive?.sentiment_distribution
  const customAnalysis = passive?.custom_analysis

  return (
    <div className="space-y-5">
      {/* Passive metrics */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('detail.eval.passiveTitle')}
        </h3>
        <div className="mt-2 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Meta
            label={t('detail.eval.callCount')}
            value={passive?.call_count != null ? String(passive.call_count) : undefined}
          />
          <Meta
            label={t('detail.eval.successRate')}
            value={successRate != null ? formatRate(successRate) : undefined}
          />
        </div>

        {sentiment && Object.keys(sentiment).length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-text-muted">{t('detail.eval.sentiment')}</p>
            <div className="mt-1 flex flex-wrap gap-2">
              {Object.entries(sentiment).map(([k, v]) => (
                <Badge key={k} tone={sentimentTone(k)}>
                  {humanizeKey(k)}: {v}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {customAnalysis && Object.keys(customAnalysis).length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-text-muted">{t('detail.eval.customOutputs')}</p>
            <div className="mt-1 space-y-3">
              {Object.entries(customAnalysis).map(([key, values]) => (
                <div key={key}>
                  <p className="text-xs font-medium text-text-secondary">{humanizeKey(key)}</p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {values && typeof values === 'object' && Object.keys(values).length > 0 ? (
                      Object.entries(values).map(([val, count]) => (
                        <Badge key={val} tone="neutral">
                          {val}: {count}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-xs italic text-text-muted">—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(passive?.call_count == null || passive.call_count === 0) &&
          (!sentiment || Object.keys(sentiment).length === 0) &&
          (!customAnalysis || Object.keys(customAnalysis).length === 0) && (
            <EmptyLine label={t('detail.eval.noPassive')} />
          )}
      </div>

      {/* Active critiques */}
      <div className="border-t border-border pt-4">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('detail.eval.activeTitle')}
        </h3>
        {active.length > 0 ? (
          <ul className="mt-2 space-y-3">
            {active.map((critique) => (
              <li key={critique.id} className="rounded-lg border border-border bg-surface p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-medium text-text-primary">
                    {critique.summary || t('detail.eval.untitledCritique')}
                  </span>
                  {critique.created_at != null && (
                    <span className="text-xs text-text-muted">{formatEpoch(critique.created_at)}</span>
                  )}
                </div>
                <Scores verdict={critique.verdict} />
                {critique.call_id && (
                  <p className="mt-2 font-mono text-xs text-text-muted">
                    {t('detail.eval.callRef', { id: critique.call_id })}
                  </p>
                )}
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-text-secondary hover:text-primary">
                    {t('detail.eval.rawVerdict')}
                  </summary>
                  <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-surface-card p-2 text-xs text-text-secondary">
                    {safeJson(critique.verdict)}
                  </pre>
                </details>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyLine label={t('detail.eval.noActive')} />
        )}
      </div>
    </div>
  )
}

/** Best-effort rubric-score extraction: if the verdict carries a `scores`
 *  object (or is itself a flat map of number values), render them as chips. */
function Scores({ verdict }: { verdict: unknown }) {
  const scores = extractScores(verdict)
  if (!scores || Object.keys(scores).length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {Object.entries(scores).map(([k, v]) => (
        <Badge key={k} tone="info">
          {humanizeKey(k)}: {v}
        </Badge>
      ))}
    </div>
  )
}

function extractScores(verdict: unknown): Record<string, number> | null {
  if (!verdict || typeof verdict !== 'object') return null
  const obj = verdict as Record<string, unknown>
  const candidate = obj.scores ?? obj.rubric ?? obj.rubric_scores
  // Only read from a named score container — falling back to the whole verdict
  // object would render unrelated top-level numbers (e.g. `version`) as chips.
  if (!candidate || typeof candidate !== 'object') return null
  const source = candidate as Record<string, unknown>
  const out: Record<string, number> = {}
  for (const [k, v] of Object.entries(source)) {
    if (typeof v === 'number') out[k] = v
  }
  return Object.keys(out).length > 0 ? out : null
}

// --- Small shared bits ------------------------------------------------------

function Meta({
  label,
  value,
  mono = false,
}: {
  label: string
  value?: string | null
  mono?: boolean
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted">{label}</p>
      <p
        className={`mt-0.5 break-words text-sm text-text-primary ${mono ? 'font-mono' : ''}`}
      >
        {value || <span className="italic text-text-muted">—</span>}
      </p>
    </div>
  )
}

function EmptyLine({ label }: { label: string }) {
  return <p className="text-sm italic text-text-muted">{label}</p>
}

function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim()
}

function formatRate(rate: number): string {
  // Accept either a 0..1 fraction or an already-scaled percentage.
  const pct = rate <= 1 ? rate * 100 : rate
  return `${Math.round(pct)}%`
}

function sentimentTone(key: string): 'success' | 'warning' | 'danger' | 'neutral' {
  const k = key.toLowerCase()
  if (k.includes('positive')) return 'success'
  if (k.includes('negative')) return 'danger'
  if (k.includes('neutral')) return 'neutral'
  return 'warning'
}

function formatEpoch(ts: number): string {
  // Eval `created_at` is epoch seconds; some Retell fields are millis. Detect
  // by magnitude so either scale renders correctly.
  const ms = ts > 1e12 ? ts : ts * 1000
  return new Date(ms).toLocaleString()
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
